import type React from "react";

import { useState, useEffect } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import InkrivLoader from "@/assets/Inkriv-loader.gif";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Search,
  MoreVertical,
  FileText,
  Pencil,
  Trash2,
  LayoutGrid,
  List,
  CirclePlus,
  Loader2,
} from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "react-toastify";

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ApiService } from "@/services/api-service";

import { useChatActions } from "@/store/useChatStore";
import { Project } from "@/types/types";
import { useEditorActions, useViewMode } from "@/store/useEditorStore";
import { Badge } from "@/components/ui/badge";
import { FiEdit } from "react-icons/fi";

const formatDate = (dateString: string | Date) => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "--"; // Invalid date

  const months = [
    "JAN",
    "FEB",
    "MAR",
    "APR",
    "MAY",
    "JUN",
    "JUL",
    "AUG",
    "SEP",
    "OCT",
    "NOV",
    "DEC",
  ];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
};

export const Route = createFileRoute("/app/_layout/")({
  component: Home,
});

function Home() {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const viewMode = useViewMode();
  const { setViewMode } = useEditorActions();

  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Pick<
    Project,
    "id" | "name" | "created_at"
  > | null>(null);
  const [newProjectName, setNewProjectName] = useState("Untitled");
  const { ref, inView } = useInView();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    error,
    refetch,
  } = useInfiniteQuery({
    queryKey: ["projects", debouncedSearch],
    queryFn: ({ pageParam }) =>
      ApiService.fetchProjects({
        pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });

  const createProjectMutation = useMutation({
    mutationFn: ApiService.createProject,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });

      navigate({
        to: "/app/editor/$projectId",
        params: { projectId: res.project_id },
        search: {
          canceled: undefined,
          session_id: undefined,
          success: undefined,
          type: undefined,
        },
      });
    },
    onError: (error) => {
      const errMsg =
        (error as any)?.response?.data?.detail || "Failed to create document";
      console.log(error);
      toast.error(errMsg);
    },
  });

  // Rename project mutation
  const renameProjectMutation = useMutation({
    mutationFn: () =>
      ApiService.renameProject(selectedProject?.id as string, newProjectName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      setSelectedProject(null);
      setIsRenameDialogOpen(false);
    },
    onError: (error) => {
      toast.error(`Failed to rename document: ${error.message}`);
    },
  });

  // Delete project mutation
  const deleteProjectMutation = useMutation({
    mutationFn: ApiService.deleteProject,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      toast.success("Your document has been deleted successfully.");
    },
    onError: (error) => {
      toast.error(`Failed to delete document: ${error.message}`);
    },
  });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const projects = data?.pages.flatMap((page) => page.projects) || [];

  const handleRename = (
    project: Pick<Project, "id" | "name" | "created_at">
  ) => {
    setSelectedProject(project);
    setNewProjectName(project.name);
    setIsRenameDialogOpen(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm("Are you sure you want to delete this document?")) {
      deleteProjectMutation.mutate(id);
    }
  };
  const { killSession } = useChatActions();

  const handleCreateNew = () => {
    createProjectMutation.mutate();
    killSession();
  };

  const submitRenameProject = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedProject && newProjectName.trim()) {
      renameProjectMutation.mutate();
    }
  };

  const disabledButton =
    !newProjectName.trim() ||
    renameProjectMutation.isPending ||
    selectedProject?.name === newProjectName;

  return (
    <div className="w-full sm:max-w-7xl min-h-screen mx-auto">
      <Card className="w-full border min-h-[80vh] shadow-2xl">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm sm:text-base font-bold">
            Your Documents
          </CardTitle>
          <Button
            onClick={handleCreateNew}
            className="bg-secondary-50 hover:bg-secondary-100 dark:bg-secondary-700 dark:text-secondary-50 dark:hover:bg-secondary-800 text-secondary-600 flex items-center justify-center cursor-pointer text-xs"
            disabled={createProjectMutation.isPending}
          >
            {createProjectMutation.isPending ? (
              <Loader2 className=" animate-spin duration-700 h-4 w-4" />
            ) : (
              <CirclePlus className="h-4 w-4" />
            )}
            <span>Create New</span>
          </Button>
        </CardHeader>

        <CardContent>
          <div className="flex flex-col space-y-4">
            {/* Search bar */}
            {/* <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 text-xs"
              />
            </div> */}

            {/* View toggle and document count */}
            <div className="flex items-center justify-between">
              <div className="font-satoshi">List of past documents</div>
              <div className="flex space-x-2">
                <Button
                  variant={viewMode === "grid" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setViewMode("grid")}
                  className="h-8 w-8 p-0"
                >
                  <LayoutGrid className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === "list" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setViewMode("list")}
                  className="h-8 w-8 p-0"
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Loading state */}
            {status === "pending" && (
              <div className="min-h-[40vh] flex items-center justify-center flex-col">
                <img src={InkrivLoader} alt="loader icon" />
                <span className="text-base text-neutral-700">Loading...</span>
              </div>
            )}

            {/* Error state */}
            {status === "error" && (
              <div className="py-8 flex flex-col items-center justify-center gap-3">
                <div className="rounded-full bg-red-100 p-3 w-12 h-12 flex items-center justify-center mb-1">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-red-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <p className="text-gray-800 font-medium">
                  Unable to load documents
                </p>
                <p className="text-gray-500 text-sm mb-2">
                  {error?.message ||
                    "There was an issue retrieving your documents"}
                </p>
                <button
                  onClick={() => refetch()}
                  className="mt-1 px-4 py-2 bg-primary-700 hover:bg-primary-500 text-white rounded-md transition-colors text-sm flex items-center gap-2"
                >
                  Try Again
                </button>
              </div>
            )}

            {/* Empty state */}
            {status === "success" && projects.length === 0 && (
              <div className="py-8 text-center">
                <FileText className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                <p className="text-gray-500">No documents found</p>
                <Button
                  onClick={handleCreateNew}
                  variant="outline"
                  className="mt-4"
                >
                  Create your first document
                </Button>
              </div>
            )}

            {/* Documents list view */}
            {status === "success" &&
              projects.length > 0 &&
              viewMode === "list" && (
                <Table className="border-separate border-spacing-y-2 ">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead></TableHead>
                      <TableHead></TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {projects.map((project) => (
                      <TableRow
                        key={project.id}
                        className="bg-neutral-50 dark:bg-neutral-900/30  hover:bg-neutral-200 transition-all duration-300 my-2"
                        // onMouseEnter={async () => {
                        //   await queryClient.prefetchQuery({
                        //     queryKey: [`project-${project.id}`],
                        //     queryFn: () =>
                        //       ApiService.getSingleProject(project.id as string),
                        //   });
                        // }}
                      >
                        <TableCell
                          className="font-medium cursor-pointer rounded-l-xl "
                          onClick={() => {
                            navigate({
                              to: "/app/editor/$projectId",
                              params: { projectId: project.id },
                              search: {
                                canceled: undefined,
                                session_id: undefined,
                                success: undefined,
                                type: project.generation_is_save_for_later
                                  ? "generate-draft"
                                  : undefined,
                              },
                            });
                            killSession();
                          }}
                        >
                          <div className="flex items-center">
                            <FileText className="h-4 w-4 mr-2 text-gray-500" />
                            <span className="block sm:hidden">
                              {project.name?.length > 20
                                ? `${project.name.substring(0, 20)}...`
                                : project.name}
                            </span>
                            <span className="hidden sm:block">
                              {project.name}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {project.generation_is_save_for_later && (
                            <Badge
                              variant="secondary"
                              className="bg-blue-100 text-blue-600 hover:bg-blue-200 text-[12px]"
                            >
                              Draft saved — not yet generated
                            </Badge>
                          )}
                        </TableCell>

                        <TableCell></TableCell>
                        <TableCell className="rounded-r-xl">
                          {project.current_content_updated_at
                            ? formatDate(project.current_content_updated_at)
                            : formatDate(project.created_at)}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" className="h-8 w-8 p-0">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => handleRename(project)}
                              >
                                <FiEdit className="mr-2 h-4 w-4" />
                                <span>Rename</span>
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleDelete(project.id)}
                                className="text-red-600"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                <span>Delete</span>
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}

                    {/* Loading more indicator */}
                    <TableRow ref={ref}>
                      <TableCell colSpan={4} className="h-24 text-center">
                        {isFetchingNextPage && (
                          <Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" />
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              )}

            {/* Documents grid view */}
            {status === "success" &&
              projects.length > 0 &&
              viewMode === "grid" && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {projects.map((project) => (
                    <Card
                      key={project.id}
                      className="overflow-hidden relative"
                      // onMouseEnter={async () => {
                      //   await queryClient.prefetchQuery({
                      //     queryKey: [`project-${project.id}`],
                      //     queryFn: () =>
                      //       ApiService.getSingleProject(project.id as string),
                      //   });
                      // }}
                    >
                      <CardContent className="p-4">
                        <div
                          className="flex flex-col justify-center items-center gap-y-2 cursor-pointer"
                          onClick={() => {
                            navigate({
                              to: "/app/editor/$projectId",
                              params: { projectId: project.id },
                              search: {
                                canceled: undefined,
                                session_id: undefined,
                                success: undefined,
                                type: undefined,
                              },
                            });
                            killSession();
                          }}
                        >
                          <FileText className="h-5 w-5 mr-2 text-gray-500" />
                          <span className="font-medium truncate p-4">
                            {project.name?.length > 30
                              ? `${project.name.substring(0, 30)}...`
                              : project.name}
                          </span>
                        </div>
                        <div className="absolute top-2 right-2">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" className="h-8 w-8 p-0">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => handleRename(project)}
                              >
                                <FiEdit className="mr-2 h-4 w-4" />
                                <span>Rename</span>
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleDelete(project.id)}
                                className="text-red-600"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                <span>Delete</span>
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>

                        <div className="mt-2 flex flex-col gap-y-2 items-center">
                          <div>
                            {project.generation_is_save_for_later && (
                              <Badge
                                variant="secondary"
                                className="bg-blue-100 text-blue-600 hover:bg-blue-200"
                              >
                                Draft saved — not yet generated
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">
                            {project.current_content_updated_at
                              ? formatDate(project.current_content_updated_at)
                              : formatDate(project.created_at)}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}

                  {/* Loading more indicator */}
                  <div
                    ref={ref}
                    className="col-span-full h-24 flex items-center justify-center"
                  >
                    {isFetchingNextPage && (
                      <Loader2 className="h-6 w-6 animate-spin text-blue-700" />
                    )}
                  </div>
                </div>
              )}
          </div>
        </CardContent>

        {/* Rename Document Dialog */}
        <Dialog open={isRenameDialogOpen} onOpenChange={setIsRenameDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className=""></DialogTitle>
            </DialogHeader>
            <form onSubmit={submitRenameProject}>
              <div className="grid gap-4 sm:py-4 mb-4">
                <div className="grid gap-2">
                  <Input
                    value={newProjectName}
                    onChange={(e) => setNewProjectName(e.target.value)}
                    autoFocus
                    placeholder="document name...."
                    className="text-xs font-satoshi"
                  />
                </div>
              </div>
              <DialogFooter className="flex flex-row">
                <Button
                  type="button"
                  variant="outline"
                  className="text-xs sm:text-sm font-satoshi"
                  onClick={() => setIsRenameDialogOpen(false)}
                >
                  Cancel
                </Button>
                <button
                  type="submit"
                  className={`bg-secondary-500 hover:bg-secondary-600 text-xss sm:text-xs px-4 rounded-sm text-white cursor-pointer flex items-center ${disabledButton ? "cursor-not-allowed" : ""}`}
                  disabled={disabledButton}
                >
                  {renameProjectMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </Card>
    </div>
  );
}
